import { errorLogger } from './trackingUtil';
import { sleep } from './helperFunction';

import { DEVICE_KIND, DEVICE_LOCALSTORAGE_KEY } from '../constants/Constants';

/** @param {string} raw - Raw JSON string from localStorage. @returns {object} Parsed object, or {} on failure. */
const safeParse = raw => {
  try {
    return JSON.parse(raw || '{}') || {};
  } catch (e) {
    errorLogger(e, { location: 'mediaDeviceUtils.safeParse' });
    return {};
  }
};

/** @returns {object} All stored devices keyed by DEVICE_KIND (mic/webcam/speaker). */
export const getStoredDevices = () =>
  safeParse(localStorage.getItem(DEVICE_LOCALSTORAGE_KEY));

/** @param {string} kind - DEVICE_KIND value. @returns {MediaDeviceInfo|null} Stored device or null. */
export const getStoredDevice = kind => getStoredDevices()[kind] || null;

/** @param {string} kind - DEVICE_KIND value. @returns {string|null} Stored deviceId or null. */
export const getStoredDeviceId = kind => {
  const device = getStoredDevice(kind);
  return device && device.deviceId ? device.deviceId : null;
};

export const STORED_DEVICE_CHANGE_EVENT = 'doselect:stored-device-change';

// How often to re-validate the live webcam stream while the user is in an
// assessment. Tweak here to change the cadence everywhere.
export const WEBCAM_STREAM_VALIDATION_INTERVAL_MS = 5000;

/**
 * Persists the selected device for a given kind to localStorage and fires a
 * `doselect:stored-device-change` custom event so listeners (e.g. AudioPlayerComponent)
 * can react immediately without polling.
 *
 * @param {string}          kind   - DEVICE_KIND value (mic/webcam/speaker).
 * @param {MediaDeviceInfo} device - Device to store; must have a valid deviceId.
 */
export const setStoredDevice = (kind, device) => {
  if (!device || !device.deviceId) return;
  const stored = getStoredDevices();
  localStorage.setItem(
    DEVICE_LOCALSTORAGE_KEY,
    JSON.stringify({ ...stored, [kind]: device })
  );
  if (
    typeof window !== 'undefined' &&
    typeof window.dispatchEvent === 'function'
  ) {
    try {
      window.dispatchEvent(
        new CustomEvent(STORED_DEVICE_CHANGE_EVENT, {
          detail: { kind, deviceId: device.deviceId },
        })
      );
    } catch (e) {
      errorLogger(e, { location: 'mediaDeviceUtils.setStoredDevice', kind });
    }
  }
};

/**
 * Merges a stored deviceId with caller-supplied constraint overrides.
 * The stored deviceId is used as the default; an explicit `deviceId` in
 * overrides always wins so callers can target a specific device for a probe.
 *
 * @param {string|null} storedDeviceId - Previously stored deviceId, or null.
 * @param {object|null} overrides      - Additional getUserMedia constraints.
 * @returns {object|true} Merged constraint object, or `true` for browser default.
 */
const mergeAudioVideoConstraints = (storedDeviceId, overrides) => {
  if (overrides && typeof overrides === 'object') {
    if (Object.prototype.hasOwnProperty.call(overrides, 'deviceId')) {
      return { ...overrides };
    }
    return storedDeviceId
      ? { ...overrides, deviceId: { exact: storedDeviceId } }
      : { ...overrides };
  }
  return storedDeviceId ? { deviceId: { exact: storedDeviceId } } : true;
};

/** @param {object|null} overrides - Extra audio constraints. @returns {object|true} Merged audio constraints using the stored mic deviceId. */
export const buildAudioConstraints = (overrides = null) =>
  mergeAudioVideoConstraints(getStoredDeviceId(DEVICE_KIND.MIC), overrides);

/** @param {object|null} overrides - Extra video constraints. @returns {object|true} Merged video constraints using the stored webcam deviceId. */
export const buildVideoConstraints = (overrides = null) =>
  mergeAudioVideoConstraints(getStoredDeviceId(DEVICE_KIND.WEBCAM), overrides);

/**
 * Builds a combined getUserMedia constraints object for audio and/or video,
 * injecting the stored deviceIds automatically.
 *
 * @param {boolean|null} audio         - true to include audio, false to explicitly disable.
 * @param {boolean|null} video         - true to include video, false to explicitly disable.
 * @param {object|null}  audioOverrides - Extra audio constraints forwarded to buildAudioConstraints.
 * @param {object|null}  videoOverrides - Extra video constraints forwarded to buildVideoConstraints.
 * @returns {{ audio?: object|boolean, video?: object|boolean }}
 */
export const getMediaConstraints = ({
  audio,
  video,
  audioOverrides = null,
  videoOverrides = null,
} = {}) => {
  const constraints = {};
  if (audio) {
    constraints.audio = buildAudioConstraints(audioOverrides);
  } else if (audio === false) {
    constraints.audio = false;
  }
  if (video) {
    constraints.video = buildVideoConstraints(videoOverrides);
  } else if (video === false) {
    constraints.video = false;
  }
  return constraints;
};

/** @returns {string|null} Stored speaker deviceId, used as the audio output sink. */
export const getStoredSpeakerSinkId = () =>
  getStoredDeviceId(DEVICE_KIND.SPEAKER);

/** @param {object|null} overrides - Extra audio constraints. @returns {{ audio: object|true }} getUserMedia constraints for the stored mic. */
export const getMicConstraints = (overrides = null) => ({
  audio: buildAudioConstraints(overrides),
});

/** @param {object|null} overrides - Extra video constraints. @returns {{ video: object|true }} getUserMedia constraints for the stored webcam. */
export const getWebcamConstraints = (overrides = null) => ({
  video: buildVideoConstraints(overrides),
});

/**
 * Safely closes a Web Audio AudioContext, ignoring errors if it is already closed.
 * @param {AudioContext} ctx - The AudioContext to close.
 */
const closeAudioContext = async ctx => {
  if (ctx && ctx.state !== 'closed') {
    try {
      await ctx.close();
    } catch (e) {
      errorLogger(e, { location: 'mediaDeviceUtils.closeAudioContext' });
    }
  }
};

/**
 * Continuously polls a microphone stream and measures audio activity via RMS.
 * Runs until the provided AbortSignal is aborted (e.g. when the user clicks Stop).
 * Each poll emits a result via `onUpdate` indicating whether the mic is active or silent.
 * Resolves with the last emitted result once the signal aborts.
 *
 * Early exits (before polling starts) if the stream has no audio track, the track
 * is not live, or the track is muted.
 *
 * @param {MediaStream}  stream                        - The mic stream to validate.
 * @param {number}       [options.thresholdRms=0.005]  - Minimum RMS to consider the mic active.
 * @param {number}       [options.pollIntervalMs=200]  - Interval between RMS samples in ms.
 * @param {AbortSignal}  [options.signal]              - Signal to stop the polling loop.
 * @param {Function}     [options.onUpdate]            - Called with each { valid, reason, peakRms } result.
 * @returns {Promise<{valid: boolean, reason: string|null, peakRms: number}>}
 */
export const validateMicStream = async (
  stream,
  { thresholdRms = 0.005, pollIntervalMs = 200, signal, onUpdate } = {}
) => {
  const emit = result => {
    if (typeof onUpdate === 'function') {
      try {
        onUpdate(result);
      } catch (e) {
        errorLogger(e, { location: 'mediaDeviceUtils.validateMicStream.emit' });
      }
    }
    return result;
  };

  if (!stream || stream.getAudioTracks().length === 0) {
    return emit({ valid: false, reason: 'no-audio-track', peakRms: 0 });
  }
  const track = stream.getAudioTracks()[0];
  if (track.readyState !== 'live') {
    return emit({ valid: false, reason: 'not-live', peakRms: 0 });
  }
  if (track.muted) {
    return emit({ valid: false, reason: 'muted', peakRms: 0 });
  }

  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) {
    return emit({
      valid: false,
      reason: 'audio-context-unavailable',
      peakRms: 0,
    });
  }

  let context;
  try {
    context = new AudioCtx();
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);

    const buffer = new Uint8Array(analyser.fftSize);
    let latest = emit({ valid: false, reason: 'silent', peakRms: 0 });

    while (!signal || !signal.aborted) {
      analyser.getByteTimeDomainData(buffer);
      let sumSq = 0;
      for (let i = 0; i < buffer.length; i += 1) {
        const sample = (buffer[i] - 128) / 128;
        sumSq += sample * sample;
      }
      const rms = Math.sqrt(sumSq / buffer.length);
      const valid = rms >= thresholdRms;
      latest = emit({
        valid,
        reason: valid ? null : 'silent',
        peakRms: rms,
      });
      // eslint-disable-next-line no-await-in-loop
      await sleep(pollIntervalMs);
    }
    return latest;
  } catch (error) {
    errorLogger(error, {
      location: 'mediaDeviceUtils.validateMicStream',
      reason: 'analyser-error',
    });
    return emit({ valid: false, reason: 'analyser-error', error, peakRms: 0 });
  } finally {
    await closeAudioContext(context);
  }
};

/**
 * Samples a single video frame and returns its mean luminance and variance.
 * Used by `validateWebcamStream` to detect blank (low variance) or frozen
 * (low mean delta across frames) webcam feeds.
 *
 * Pixels are sampled at a stride rather than every pixel for performance —
 * stride is chosen so ~1024 samples are taken regardless of resolution.
 *
 * @param {CanvasRenderingContext2D} ctx2d   - 2D canvas context to draw into.
 * @param {HTMLVideoElement}         video   - Video element playing the stream.
 * @param {number}                   width   - Canvas/frame width in pixels.
 * @param {number}                   height  - Canvas/frame height in pixels.
 * @returns {{ mean: number, variance: number }} Mean and variance of luminance values (0–255).
 */
const computeFrameStats = (ctx2d, video, width, height) => {
  ctx2d.drawImage(video, 0, 0, width, height);
  const { data } = ctx2d.getImageData(0, 0, width, height);
  const stride = Math.max(4, Math.floor(data.length / 1024 / 4) * 4);
  let mean = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += stride) {
    mean += (data[i] + data[i + 1] + data[i + 2]) / 3;
    count += 1;
  }
  mean /= count || 1;
  let variance = 0;
  for (let i = 0; i < data.length; i += stride) {
    const lum = (data[i] + data[i + 1] + data[i + 2]) / 3;
    variance += (lum - mean) ** 2;
  }
  variance /= count || 1;
  return { mean, variance };
};

/**
 * Opens a short-lived webcam stream and takes multiple frame samples to detect
 * if the feed is blank (all-black/white) or frozen (no pixel movement).
 *
 * Samples frames over `durationMs` at `sampleStrideMs` intervals, then:
 * - Returns `blank` if every frame's variance is below `blankVarianceThreshold`.
 * - Returns `frozen` if the total mean luminance delta across frames is below `minMeanDelta`.
 * - Returns `valid: true` if neither condition is met.
 *
 * @param {MediaStream} stream                              - Webcam stream to validate.
 * @param {number}      [options.durationMs=800]            - Total sampling window in ms.
 * @param {number}      [options.sampleStrideMs=200]        - Interval between frame samples in ms.
 * @param {number}      [options.blankVarianceThreshold=5]  - Max variance to classify a frame as blank.
 * @param {number}      [options.minMeanDelta=0.4]          - Min cumulative mean delta to classify as not frozen.
 * @returns {Promise<{valid: boolean, reason: string|null, samples?: object[]}>}
 */
export const validateWebcamStream = async (
  stream,
  {
    durationMs = 800,
    sampleStrideMs = 200,
    blankVarianceThreshold = 5,
    // minMeanDelta = 0.4,
  } = {}
) => {
  if (!stream || stream.getVideoTracks().length === 0) {
    return { valid: false, reason: 'no-video-track' };
  }
  const track = stream.getVideoTracks()[0];
  if (track.readyState !== 'live') {
    return { valid: false, reason: 'not-live' };
  }
  if (track.muted) {
    return { valid: false, reason: 'muted' };
  }

  const video = document.createElement('video');
  video.srcObject = stream;
  video.muted = true;
  video.playsInline = true;

  try {
    await video.play();
  } catch (error) {
    errorLogger(error, {
      location: 'mediaDeviceUtils.validateWebcamStream',
      reason: 'play-failed',
    });
    return { valid: false, reason: 'play-failed', error };
  }

  const settings = (track.getSettings && track.getSettings()) || {};
  const width = video.videoWidth || settings.width || 320;
  const height = video.videoHeight || settings.height || 240;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) {
    video.pause();
    video.srcObject = null;
    return { valid: false, reason: 'canvas-unavailable' };
  }

  const samples = [];
  const start = performance.now();
  while (performance.now() - start < durationMs) {
    samples.push(computeFrameStats(ctx2d, video, width, height));
    // eslint-disable-next-line no-await-in-loop
    await sleep(sampleStrideMs);
  }

  video.pause();
  video.srcObject = null;

  if (samples.length === 0) {
    return { valid: false, reason: 'no-samples' };
  }

  const allBlank = samples.every(s => s.variance < blankVarianceThreshold);
  if (allBlank) {
    return { valid: false, reason: 'blank', samples };
  }

  // if (samples.length >= 2) {
  //   const meanDelta = samples.reduce(
  //     (acc, sample, i) =>
  //       i === 0 ? acc : acc + Math.abs(sample.mean - samples[i - 1].mean),
  //     0
  //   );
  //   if (meanDelta < minMeanDelta) {
  //     return { valid: false, reason: 'frozen', samples, meanDelta };
  //   }
  // }

  return { valid: true, samples };
};

/**
 * Stops all tracks in a MediaStream. Safe to call with null/undefined.
 * @param {MediaStream} stream - The stream whose tracks should be stopped.
 */
export const stopMediaStream = stream => {
  if (!stream || typeof stream.getTracks !== 'function') return;
  stream.getTracks().forEach(track => {
    try {
      track.stop();
    } catch (e) {
      errorLogger(e, { location: 'mediaDeviceUtils.stopMediaStream' });
    }
  });
};

/**
 * Enumerates currently available microphone and webcam devices.
 * Labels are only populated once mic/webcam permission has been granted for
 * this origin; before that they come back as empty strings.
 * @returns {Promise<Array<{kind: string, deviceId: string, groupId: string, label: string}>>}
 */
export const listAvailableMediaDevices = async () => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter(d => d.kind === 'audioinput' || d.kind === 'videoinput')
      .map(d => ({
        kind: d.kind,
        deviceId: d.deviceId,
        groupId: d.groupId,
        label: d.label || 'unknown',
      }));
  } catch (e) {
    errorLogger(e, { location: 'mediaDeviceUtils.listAvailableMediaDevices' });
    return [];
  }
};
